import React, { useState, useRef, ChangeEvent, FocusEvent } from 'react';
import Grid from '@mui/material/Grid';
import AddBeneficiaryDrawer from '../../AddBeneficiaryDrawer';
import { useTheme } from '@mui/material/styles';
import { PaymentPurposesInterface } from '../../../../../customhooks/paymentpurpose/usePaymentpurposes';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { REACT_APP_BASE_URL } from '../../../../../config';
import useWallets from '../../../../../customhooks/wallets/useWallets';
import Skeleton from '@mui/material/Skeleton';
import { Beneficiary } from '../../interfaces/Beneficiary';
import { useMediaQuery } from '@mui/material';
import { PaymentRequest } from '../../interfaces/PaymentRequest';
import { validateEmail } from '../../../../../utils/helper';
import UploadItem from '../../../../../components/UploadItem';
import AutoCompleteSelect from '../../../../../components/AutoCompleteSelect';
import { PlusIcon } from '../../../../../components/icons/PlusIcon';
import Input from '../../../../../components/input/Input';
import Button from '../../../../../components/button/Button';

export interface SelectBeneficiaryProps {
    currencyFrom: string;
    currencyTo: string;
    activeStep: number;
    updateStep: (activeStep: number) => void;
    conversionType: number;
    requestObject: PaymentRequest;
    updateRequestObject: (value: PaymentRequest) => void;
    updateRecipientChoosen: (value: any) => void;
    updateConversionType: (value: number) => void;
    beneficiaries: Beneficiary[];
    isBeneficiariesLoading: boolean;
    fetchBeneficiaries: () => void;
    paymentPurposes: PaymentPurposesInterface[];
    loadingPaymentPurposes: boolean;
}

interface FormErrors {
    recipientId: string;
    purposeId: string;
    reference: string;
    transactionDocumentsUrl: string;
    notifyParties: string;
}

const SelectBeneficiary = ({
    currencyFrom,
    currencyTo,
    activeStep,
    updateStep,
    requestObject,
    updateRequestObject,
    conversionType,
    updateRecipientChoosen,
    updateConversionType,
    beneficiaries,
    isBeneficiariesLoading,
    fetchBeneficiaries,
    paymentPurposes,
    loadingPaymentPurposes
}: SelectBeneficiaryProps) => {
    const { wallets, isLoading: isWalletsLoading } = useWallets();

    const theme = useTheme();

    const uploadRef = useRef<any>(null);

    const { breakpoints } = useTheme();
    const isMobile = useMediaQuery(breakpoints.down('md'));

    const [errors, setErrors] = useState<FormErrors>({} as FormErrors);

    const [showAddBeneficiary, setShowAddBeneficiary] = useState(false);
    const [beneficiaryType, setBeneficiaryType] = useState(
        'beneficiary-account'
    );

    const updateField = (name: string, value: any) => {
        setErrors((prevState) => ({
            ...prevState,
            [name]: ''
        }));

        const newRequest = { ...requestObject };

        updateRequestObject({
            ...requestObject,
            ...newRequest,
            [name]: value
        });
    };

    /**
     * Validates the inputs and move to the next step
     */
    const validateToNextStep = () => {
        const newErrors = {
            ...errors,
            recipientId: '',
            purposeId: '',
            transactionDocumentsUrl: '',
            notifyParties: ''
        };

        newErrors['recipientId'] = '';
        newErrors['purposeId'] = '';
        newErrors['transactionDocumentsUrl'] = '';
        newErrors['notifyParties'] = '';

        if (beneficiaryType === 'beneficiary-account') {
            if (requestObject.recipientId === undefined) {
                newErrors.recipientId = 'Recipient is required';
            }

            if (requestObject.purposeId === undefined) {
                newErrors.purposeId = 'Purpose is required';
            }
        }
        if (requestObject.transactionDocumentsUrl === undefined) {
            newErrors.transactionDocumentsUrl = 'Document is required';
        }

        if (
            requestObject?.notifyParties?.length &&
            !requestObject?.notifyParties?.every((item) => validateEmail(item))
        ) {
            newErrors.notifyParties =
                'All parties should be a valid email address';
        }

        setErrors((prevState) => ({
            ...prevState,
            ...newErrors
        }));

        if (Object.values(newErrors).every((item) => item === '')) {
            updateStep(activeStep + 1);
        }
    };

    const closeAddBeneficiaryDrawer = () => {
        setShowAddBeneficiary(false);
    };

    const filterBeneficiary = (beneficiaries: Beneficiary[]) => {
        const filteredData = beneficiaries?.filter(
            (item: Beneficiary) => item.currencyCode === currencyTo
        );
        return filteredData?.map((item) => {
            return {
                label: `${item.beneficiaryCompanyName} - ${item.accountNumber}`,
                value: String(item.id)
            };
        });
    };

    const formattedPurposes = paymentPurposes.map((item) => {
        return {
            label: item.title,
            value: String(item.id)
        };
    });
    const handleChangeBeneficiaryType = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const valueChoosen = (event.target as HTMLInputElement).value;
        setBeneficiaryType(valueChoosen);
        if (valueChoosen === 'own-account') {
            updateConversionType(2);

            const checkCurrency = wallets.filter(
                (item) => item.currency === currencyTo
            );
            updateRecipientChoosen(checkCurrency[0]);
        } else {
            updateConversionType(1);
            updateRecipientChoosen(null);
        }
    };

    const userHasReceiveWallet = () => {
        const checkCurrency = wallets.filter(
            (item) => item.currency === currencyTo
        );
        if (checkCurrency.length > 0) {
            return true;
        }
        return false;
    };

    const validateReference = (reference: string): boolean => {
        if (reference.trim() === '') {
            return true;
        }

        if (/^[a-zA-Z0-9]+$/.test(reference) && reference.length <= 32) {
            return true;
        } else {
            setErrors((errors) => ({
                ...errors,
                reference:
                    'Must be 32 characters or less and contain only alphanumeric characters'
            }));
            return false;
        }
    };

    return (
        <div className='max-w-[500px]'>
            {currencyFrom !== currencyTo && (
                <div className='px-[16px] lg:px-0'>
                    <FormControl>
                        <RadioGroup
                            row
                            aria-labelledby='beneficiary-select-type'
                            name='beneficiary-type'
                            value={beneficiaryType}
                            onChange={handleChangeBeneficiaryType}
                        >
                            <FormControlLabel
                                value='beneficiary-account'
                                control={<Radio />}
                                label='A beneficiary account'
                            />

                            {isWalletsLoading ? (
                                <>
                                    <div className='w-[10rem]'>
                                        <Skeleton
                                            variant='text'
                                            sx={{ fontSize: '2em' }}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    {userHasReceiveWallet() ? (
                                        <FormControlLabel
                                            value='own-account'
                                            control={<Radio />}
                                            label={`My ${currencyTo} account`}
                                        />
                                    ) : null}
                                </>
                            )}
                        </RadioGroup>
                    </FormControl>
                </div>
            )}

            <div className='relative mt-4 lg:mt-8 px-[16px] lg:px-0'>
                <Grid
                    container
                    spacing={4}
                    sx={{
                        marginTop: '0px'
                    }}
                >
                    <Grid item xs={12} md={12} lg={12}>
                        <div>
                            <AutoCompleteSelect
                                id='beneficiary-purpose'
                                label='Purpose'
                                required={
                                    beneficiaryType === 'beneficiary-account'
                                        ? true
                                        : false
                                }
                                options={formattedPurposes}
                                value={formattedPurposes?.find(
                                    (item) =>
                                        item.value === requestObject.purposeId
                                )}
                                isLoading={loadingPaymentPurposes}
                                onChange={({
                                    value
                                }: {
                                    label: string;
                                    value: string;
                                }) => {
                                    if (value !== null) {
                                        updateField('purposeId', value);
                                        setErrors((errors) => ({
                                            ...errors,
                                            purposeId: ''
                                        }));
                                    }
                                }}
                                name='purposeId'
                                error={!!errors?.purposeId}
                                helperText={errors?.purposeId}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={12} md={12} lg={12}>
                        <div className='mt-6'>
                            <UploadItem
                                actionText='Click to upload a supporting document'
                                action={`${REACT_APP_BASE_URL}/files/upload`}
                                accept='.png, .jpg, .jpeg, .pdf'
                                name='file'
                                maxCount={1}
                                error={errors.transactionDocumentsUrl}
                                defaultFileList={
                                    requestObject.transactionDocumentsUrl
                                        ? [
                                              {
                                                  uid: '1',
                                                  name:
                                                      requestObject.transactionDocumentsName ||
                                                      '',
                                                  status: 'done',
                                                  url: requestObject.transactionDocumentsUrl
                                              }
                                          ]
                                        : []
                                }
                                onChange={(file) => {
                                    if (file.status !== 'uploading') {
                                        updateRequestObject({
                                            ...requestObject,
                                            transactionDocumentsUrl:
                                                file.response.data.fileUri,
                                            transactionDocumentsName: file.name
                                        });
                                        setErrors((errors) => ({
                                            ...errors,
                                            transactionDocumentsUrl: ''
                                        }));
                                    }
                                }}
                            />
                        </div>
                    </Grid>
                </Grid>
            </div>

            <div className='mt-[24px] px-[16px] lg:px-0'>
                <Input
                    id='beneficiary-reference'
                    label='Add reference (optional)'
                    name='reference'
                    errorMessage={errors?.reference}
                    value={requestObject.reference}
                    onBlur={(e: FocusEvent<HTMLInputElement>) => {
                        validateReference(e.target.value);
                    }}
                    isError={!!errors?.reference}
                    required
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        updateField('reference', e.target.value);
                    }}
                />
            </div>

            {beneficiaryType === 'beneficiary-account' && (
                <>
                    <div className='mt-4 lg:mt-8 px-[16px] lg:px-0'>
                        <AutoCompleteSelect
                            id='beneficiary-beneficiaries'
                            label={`Beneficiary (${currencyTo})`}
                            required={
                                beneficiaryType === 'beneficiary-account'
                                    ? true
                                    : false
                            }
                            options={filterBeneficiary(beneficiaries)}
                            value={filterBeneficiary(beneficiaries)?.find(
                                (item) =>
                                    item.value === requestObject.recipientId
                            )}
                            isLoading={isBeneficiariesLoading}
                            onChange={({
                                value
                            }: {
                                label: string;
                                value: string;
                            }) => {
                                if (value !== null) {
                                    const recipientChoosen =
                                        beneficiaries?.find(
                                            (item: Beneficiary) =>
                                                String(item.id) === value
                                        );

                                    updateRecipientChoosen(recipientChoosen);
                                    updateField('recipientId', value);
                                    updateConversionType(1);
                                }
                            }}
                            name='recipientId'
                            error={!!errors?.recipientId}
                            helperText={errors?.recipientId}
                        />
                    </div>

                    <div className=' px-[16px] lg:px-0'>
                        <p className='my-4 text-paleSky font-medium text-sm'>
                            or
                        </p>
                        <Button
                            text='Add beneficiary'
                            onClick={() => {
                                setShowAddBeneficiary(true);
                            }}
                            variant='secondaryOutline'
                            leftIcon={<PlusIcon />}
                            className='border w-full border-gray300 text-steelBlue font-medium text-base leading-[24px]'
                        />
                    </div>
                </>
            )}

            <div className='mt-[24px] px-[16px] lg:px-0'>
                <Input
                    label='Notifying Parties'
                    variant='tags'
                    isError={!!errors?.notifyParties}
                    errorMessage={errors?.notifyParties}
                    tagValues={requestObject.notifyParties}
                    onChangeTagValues={(val) => {
                        if (val) {
                            updateRequestObject({
                                ...requestObject,
                                notifyParties: val
                            });

                            setErrors((errors) => ({
                                ...errors,
                                notifyParties: ''
                            }));
                        }
                    }}
                />

                <div className='text-midnightHaze text-sm  lg:text-[13px]  mt-1'>
                    This input supports multiple email entries, press enter when
                    done to start a new entry.
                </div>
            </div>

            <div className='relative mt-4 px-[16px] lg:px-0'>
                <div className='flex gap-[16px] items-center justify-end mt-8 mb-[80px] lg:mb-0'>
                    <Button
                        text='Back'
                        onClick={() => {
                            updateStep(activeStep - 1);
                            updateConversionType(1);
                        }}
                        variant='secondaryOutline'
                        className='px-[0.92rem] flex-1 border border-gray300 text-steelBlue font-medium text-sm leading-[20px]'
                    />
                    <Button
                        text='Continue'
                        onClick={() => validateToNextStep()}
                        variant='primary'
                        className='px-[0.92rem] flex-1'
                    />
                </div>
            </div>

            {
                <AddBeneficiaryDrawer
                    closeDrawer={closeAddBeneficiaryDrawer}
                    showDrawer={showAddBeneficiary}
                    reRunGetBeneficiaries={fetchBeneficiaries}
                    setBeneficiaryId={() => console.log()}
                />
            }
        </div>
    );
};

export default SelectBeneficiary;
